import React, { RefObject, useContext } from 'react';
import { Stack, TextField, useTheme, Input, Box, MenuItem, Typography } from '@mui/material';
import { getSuggestionAction } from '../../store/Product/action';
import { getSearchText } from '../../api/search';
import { reducerTypes } from '../../store/Product/types';
import SearchIconSVG from '../../assets/images/svg/search.svg';
import { SuggestionType } from '../../react-pages/Catalog/types';
import { useAppDispatch } from '../../hooks/reduxHooks';
import Image from 'next/image';
import { buildRoute } from '../../utils/routerPathBuilder';
import { LanguageContext } from '../../providers/LanguageProvider';

interface ResponsiveSearchBarProps {
  stackRef: RefObject<HTMLDivElement | null>;
  searchVisible: boolean;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  suggestions: SuggestionType[];
}

export const ResponsiveSearchBar: React.FC<ResponsiveSearchBarProps> = ({
  stackRef,
  searchVisible,
  searchText,
  setSearchText,
  suggestions,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const { lang } = useContext(LanguageContext);

  function goToCatalog(item: SuggestionType) {
    getSearchText(item.value);

    dispatch({ type: reducerTypes.GET_FILTERS, payload: [] });
    dispatch({ type: reducerTypes.GET_ALL_PRODUCTS, payload: [] });
    dispatch(getSuggestionAction({ columnName: 'searchText', value: item.value }));
    document.location.href = buildRoute(`/all-watches?searchText=${item.value}`, lang);
    setSearchText('');
  }

  return (
    <Stack
      ref={stackRef}
      sx={{
        background: theme.palette.background.default,
        display: 'flex',
        zIndex: 2,
        position: 'absolute',
        top: '0',
        left: '-17rem',
        width: '17rem',
        overflowX: 'hidden',
        borderRadius: 1,
        [theme.breakpoints.down('md')]: {
          left: '0',
          top: '100%',
          width: '100%',
          px: searchVisible ? 3 : 0,
          py: searchVisible ? 1 : 0,
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          marginLeft: searchVisible ? '0' : '18rem',
          transition: 'all .2s',
        }}
      >
        {searchVisible && (
          <>
            <Input
              startAdornment={<Image src={SearchIconSVG} alt='search' />}
              placeholder='Search'
              autoFocus
              sx={{ flex: 1, gap: 1, display: { xs: 'none', md: 'flex' } }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e: any) =>
                e.key === 'Enter' && goToCatalog({ columnName: 'searchText', value: e.target.value })
              }
            />
            <TextField
              InputProps={{
                startAdornment: <Image src={SearchIconSVG} alt='search' />,
              }}
              placeholder='Search'
              autoFocus
              sx={{ flex: 1, gap: 1, display: { xs: 'flex', md: 'none' } }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e: any) =>
                e.key === 'Enter' && goToCatalog({ columnName: 'searchText', value: e.target.value })
              }
            />
            <Box
              id='search-menu'
              sx={{
                maxHeight: '20rem',
                overflow: 'auto',
                boxShadow: '0px px 2px #888',
              }}
            >
              {suggestions.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => goToCatalog(item)}
                  sx={{
                    p: '.5rem',
                    cursor: 'pointer',
                    width: '100%',
                    '&:hover': {
                      background: '',
                    },
                  }}
                >
                  <Typography whiteSpace={'normal'} sx={{ flex: 1 }}>
                    {item?.value}
                  </Typography>
                </MenuItem>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Stack>
  );
};
